import { useState } from "react";
import { useCvBuilder } from "@/stores/cvBuilderStore";
import { CvField, CvSectionsEditor } from './CvDataFields';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, GraduationCap, Award, Languages, Edit3 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EducationStep = () => {
    const {
        draft, setProfileField,
        addCertification, updateCertification, removeCertification,
        addEducation, updateEducation, removeEducation,
        addLanguage, updateLanguage, removeLanguage
    } = useCvBuilder();
    const { certifications, education, languages } = draft;

    const [customLevelModes, setCustomLevelModes] = useState({});

    const toggleCustomMode = (index, value) => {
        setCustomLevelModes(prev => ({ ...prev, [index]: value }));
    };

    const LANGUAGE_LEVELS = ["Débutant", "Intermédiaire", "Avancé", "Courant", "Bilingue", "Langue maternelle"];

    return (
        <div className="space-y-10" data-testid="education-step">
            {/* Education Section */}
            <section className="space-y-4">
                <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <div className="flex items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-lenvia-blue" />
                        <h3 className="text-lg font-semibold text-white">Formation</h3>
                    </div>
                    <Button variant="outline" size="sm" onClick={addEducation} className="border-white/10 text-slate-300 hover:bg-white/5 hover:text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une formation
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {education.map((edu, index) => (
                        <Card key={index} className="bg-white/5 relative group border-white/10">
                            <Button
                                variant="ghost" size="icon"
                                className="absolute top-2 right-2 h-7 w-7 text-slate-400 hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => removeEducation(index)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                            <CardContent className="p-4 space-y-3">
                                {[
                                    ['title', 'Intitulé de la formation / diplôme'],
                                    ['institution', 'École / Université'],
                                    ['year', 'Année de la formation'],
                                    ['details', 'Détails de la formation'],
                                ].map(([field, label]) => <CvField key={field} path={`education.${index}.${field}`}
                                    label={label} value={edu[field]} onChange={value => updateEducation(index, { ...edu, [field]: value })} />)
                                }
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Certifications Section */}
            <section className="space-y-4">
                <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-amber-500" />
                        <h3 className="text-lg font-semibold text-white">Certifications</h3>
                    </div>
                    <Button variant="outline" size="sm" onClick={addCertification} className="border-white/10 text-slate-300 hover:bg-white/5 hover:text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une certif
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {certifications.map((cert, index) => (
                        <Card key={index} className="bg-white/5 relative group border-white/10">
                            <Button
                                variant="ghost" size="icon"
                                className="absolute top-2 right-2 h-7 w-7 text-slate-400 hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => removeCertification(index)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                            <CardContent className="p-3 space-y-3">
                                {[
                                    ['name', 'Intitulé de la certification'],
                                    ['issuer', 'Organisme certificateur'],
                                    ['year', 'Année de la certification'],
                                ].map(([field, label]) => <CvField key={field} path={`certifications.${index}.${field}`}
                                    label={label} value={cert[field]} onChange={value => updateCertification(index, { ...cert, [field]: value })} />)
                                }
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Languages Section */}
            <section className="space-y-4">
                <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <div className="flex items-center gap-2">
                        <Languages className="h-5 w-5 text-blue-400" />
                        <h3 className="text-lg font-semibold text-white">Langues</h3>
                    </div>
                    <Button variant="outline" size="sm" onClick={addLanguage} className="border-white/10 text-slate-300 hover:bg-white/5 hover:text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une langue
                    </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {languages.map((lang, index) => (
                        <div key={index} className="flex items-center gap-2 group p-2 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 transition-all">
                            <Input
                                placeholder="Langue" data-cv-path={`languages.${index}.name`} aria-label={`languages ${index + 1} — name`} value={lang.name}
                                onChange={(e) => updateLanguage(index, { ...lang, name: e.target.value })}
                                className="h-8 text-sm font-semibold bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                            />
                            {customLevelModes[index] ? (
                                <div className="flex items-center gap-1 flex-1 min-w-[140px]">
                                    <Input
                                        placeholder="Niveau (ex: bon niveau...)"
                                        data-cv-path={`languages.${index}.level`} aria-label={`languages ${index + 1} — level`} value={lang.level}
                                        onChange={(e) => updateLanguage(index, { ...lang, level: e.target.value })}
                                        className="h-8 text-xs bg-[rgba(0,0,0,0.2)] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                                        data-testid={`language-level-input-${index}`}
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleCustomMode(index, false)}
                                        className="h-8 px-2 text-xs text-blue-400 hover:text-white shrink-0"
                                        title="Revenir à la liste déroulante"
                                    >
                                        Liste
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 flex-1 min-w-[140px]">
                                    <Select
                                        value={lang.level || undefined}
                                        onValueChange={(val) => {
                                            if (val === "__custom__") {
                                                toggleCustomMode(index, true);
                                            } else {
                                                updateLanguage(index, { ...lang, level: val });
                                            }
                                        }}
                                    >
                                        <SelectTrigger data-cv-path={`languages.${index}.level`} aria-label={`Niveau de langue ${index + 1}`} className="h-8 text-xs w-full bg-[rgba(0,0,0,0.2)] border-white/10 text-white focus:ring-lenvia-blue">
                                            <SelectValue placeholder="Niveau" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-white/10 text-white">
                                            {(lang.level && !LANGUAGE_LEVELS.includes(lang.level) ? [lang.level, ...LANGUAGE_LEVELS] : LANGUAGE_LEVELS).map((level) => (
                                                <SelectItem key={level} value={level} className="focus:bg-white/10 focus:text-white cursor-pointer">
                                                    {level}
                                                </SelectItem>
                                            ))}
                                            <SelectItem value="__custom__" className="focus:bg-white/10 text-blue-400 cursor-pointer italic">
                                                ✏️ Saisie libre...
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => toggleCustomMode(index, true)}
                                        className="h-7 w-7 text-slate-400 hover:text-blue-400 shrink-0"
                                        title="Saisir un niveau personnalisé"
                                    >
                                        <Edit3 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            )}
                            <Button
                                variant="ghost" size="icon"
                                className="h-7 w-7 text-slate-400 hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                onClick={() => removeLanguage(index)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            </section>
            <section className="space-y-4 border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white">Autres rubriques du CV</h3>
                <p className="text-sm text-slate-400">Publications, conférences et autres rubriques explicitement présentes dans le document.</p>
                <CvSectionsEditor path="additional_sections" value={draft.additional_sections}
                    onChange={value => setProfileField('additional_sections', value)} />
            </section>
        </div>
    );
};

export default EducationStep;
